import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import { Box } from '@mui/material';
import { green } from '@mui/material/colors';
import { useCallback, useContext, useEffect, useState } from 'react';
import ManagementApi from '../../../apis/ManagementApi';
import { CoursesCompleteDto } from '../../../generated-types/coursesCompleteDto';
import useApi from '../../../lib/useApi';
import InfoBlock from '../../InfoBlock/InfoBlock';
import LoadingCircle from '../../LoadingCircle/LoadingCircle';
import DataRefreshContext from '../lib/DataRefreshContext';
import ManagementGridProps from '../lib/ManagementGridProps';
import ErrorInfoBlock from './ErrorInfoBlock';

/**
 * CoursesCompleteInfoBlock Component
 *
 * Fetches the courses completion data from the backend and displays it using the InfoBlock component.
 * Shows loading and error states as necessary.
 */
const CoursesCompleteInfoBlock = (props: ManagementGridProps) => {
  // State variables for the courses completion data, loading, and error states
  const [coursesCompleteData, setCoursesCompleteData] =
    useState<CoursesCompleteDto | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);

  // Initialize the ManagementApi using the custom useApi hook
  const managementApi = useApi(ManagementApi);

  // Access the dataRefreshCounter from the context
  const { dataRefreshCounter } = useContext(DataRefreshContext);

  /**
   * Fetch the courses completion data from tbe backend
   */
  const fetchCoursesCompleteData = useCallback(async () => {
    setIsLoading(true);
    setError(false);

    try {
      // Fetch the data from the API
      const data = props.orgId
        ? await managementApi.GetCoursesCompleteWithOrgId(props.orgId)
        : await managementApi.GetCoursesComplete();
      setCoursesCompleteData(data);
    } catch (err) {
      console.error('Error fetching courses completion data:', err);
      setError(true);
    } finally {
      setIsLoading(false);
    }
  }, [managementApi, props.orgId]);

  // Fetch the courses completion data when the component mounts
  useEffect(() => {
    fetchCoursesCompleteData();
  }, [fetchCoursesCompleteData]);

  /**
   * Fetch license data when dataRefreshCounter changes
   */
  useEffect(() => {
    fetchCoursesCompleteData();
  }, [dataRefreshCounter, fetchCoursesCompleteData]);

  // Render the InfoBlock with the fetched courses completion data
  return (
    <Box position="relative" height="100%" width="100%">
      {!error ? (
        <InfoBlock
          baseId="courses-complete"
          title="Courses Complete"
          content={
            coursesCompleteData
              ? `${coursesCompleteData.completedCount} of ${coursesCompleteData.outOf} (${coursesCompleteData.formattedDecimal})`
              : ''
          }
          icon={
            <CheckCircleOutlineRoundedIcon
              sx={{ height: '28px', width: '28px' }}
            />
          }
          subtitle="Members who completed the required training."
          color={green.A400}
        />
      ) : (
        <ErrorInfoBlock
          baseId="courses-complete-error"
          title="Courses Complete"
          subtitle="Failed to load courses completion data."
        />
      )}
      <LoadingCircle isLoading={isLoading} hideText />
    </Box>
  );
};

export default CoursesCompleteInfoBlock;
